import { trackError } from '@cint/analytics';
import { Flex, Message } from '@lucidhq/lucidium';
import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryState {
  error?: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  render?: (state: Required<Pick<ErrorBoundaryState, 'error'>>) => ReactNode;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Uncaught error:', error, errorInfo);
  }

  componentDidUpdate(
    prevProps: ErrorBoundaryProps,
    prevState: ErrorBoundaryState
  ) {
    if (this.state.error && prevState.error !== this.state.error) {
      trackError(this.state.error);
    }
  }

  render() {
    const { children, render } = this.props;
    const { error } = this.state;

    if (!error) {
      return children;
    }

    return render ? (
      render({ error })
    ) : (
      <Flex
        lss={{
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Message message="Something went wrong. Please try again later" />
      </Flex>
    );
  }
}
